import type { Stage } from "@amzn/ads-forge-client";
import { ContentType as ForgeContentType } from "@amzn/ads-forge-client";
import {
    fetchAndStoreForgeApiCredentials,
    getForgeApiClient,
    NarrativeSelector,
    useForgeNarrativeDownload,
    NarrativeTranslateModalButton,
} from "@amzn/ads-forge-web-assets";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { SplitButton, SplitButtonItem } from "@amzn/storm-ui";
import { startOfHour } from "date-fns";
import { isString } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import { getBlinkApiClientV2 } from "../../api/blinkApi/blinkApi";
import { COPY_MESSAGES, TOOLTIP_MESSAGES } from "../../constants/common";
import { G4sPlacement } from "../../constants/g4sPlacement";
import { TELEMETRY_ID } from "../../constants/telemetry";
import { useSetInsightDetailComponents } from "../../hooks/insightDetail/useSetInsightDetailComponents";
import { authStore } from "../../store/auth";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { insightDetailComponentStore } from "../../store/insightDetailComponent";
import { workbenchStore } from "../../store/workbench";
import { InsightPreviewFileFormats } from "../../types/insightPreviewFileFormats";
import {
    generateSharableLinkId,
    generateShareableUrl,
} from "../../utils/api.utils";
import { copyToClipboard } from "../../utils/clipboard.utils";
import type {
    InsightDetailOption,
    InsightDetailSelectionProps,
} from "../../utils/insightDetail.utils";
import { InsightDetailType } from "../../utils/insightDetail.utils";
import {
    getFilterTelemetryDetails,
    getUnifiedFilterTelemetryDetails,
    logWithTakt,
} from "../../utils/telemetry.utils";
import { ShareIcon } from "../common/ShareIcon";
import {
    BodyComponentBuilder,
    FooterComponentBuilder,
    Header,
    HeaderCardDetails,
    HeaderComponentBuilder,
    HeaderTitle,
    IcmBusinesslineConfiguration,
} from "../common/insightDetail.components";
import { DeckDecorationWithLanguageModalButton } from "../createDeckDecoration/DeckDecorationWithLanguageModalButton";
import type { InsightFamilyDetails } from "../discovery/insights/insightFamilies/useSetInsightFamilyDetails";

import { ForgeBetaOptInToggle } from "./forgeBetaOptInToggle";

const StyledNarrativeSelectorContainer = styled.div``;

export const useSetForgeInsightFamilyDetails = (
    props: InsightFamilyDetails,
): (() => void) => {
    const {
        identifier,
        familyName,
        details,
        insightType,
        title,
        templateInfo,
        downloadLoading,
        insightFamilyTrackingId,
        businesslineId,
        icmVersion,
        reset,
        handleForgeBetaToggle,
    } = props;

    const narrativeSelectorContainerRef = React.useRef<HTMLDivElement>(null);

    const [resetStore] = useStore(insightDetailComponentStore, (state) => [
        state.reset,
    ]);
    const settings = useStore(authStore, (state) => state.settings);
    const username = useStore(authStore, (state) => state.username);
    const [areComponentsReady, setComponentsReady] = useState(false);

    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    const [numPages, setNumPages] = React.useState<number>(0);
    const { downloadNarrative, downloadCurrentRenderedNarrative } =
        useForgeNarrativeDownload();

    useEffect(() => {
        const fetchCredentials = async () => {
            await fetchAndStoreForgeApiCredentials({
                stage: settings.stage as Stage,
                username,
                cognitoPoolId: settings.cognitoPoolId,
                accountId: settings.blinkAwsAccountId,
            });
        };
        void fetchCredentials();
    }, []);

    const handleMount = (props: InsightDetailSelectionProps): void => {
        props.setSelectedSlides(getSlideIndexes(identifier));
    };

    const handleClose = () => {
        setNumPages(0);
        setComponentsReady(false); // Reset component ready state to ensure re-rendering on reopen
        resetStore();
        reset();
    };

    const handleForgeVizDownload = (identifier) => {
        logWithTakt(TELEMETRY_ID.FORGE.VIZ_DOWNLOAD, {
            identifier,
            insightType: InsightType.INSIGHT,
        });
        downloadNarrative(identifier, ForgeContentType.INSIGHT)
            .then(() => {
                logWithTakt(TELEMETRY_ID.FORGE.VIZ_DOWNLOAD_SUCCESS, {
                    identifier,
                    insightType: InsightType.INSIGHT,
                });
            })
            .catch(() => {
                logWithTakt(TELEMETRY_ID.FORGE.VIZ_DOWNLOAD_ERROR, {
                    identifier,
                    insightType: InsightType.INSIGHT,
                });
            });
    };

    const handleCustomizedDownloadView = (identifier) => {
        logWithTakt(TELEMETRY_ID.FORGE.VIZ_DOWNLOAD, {
            identifier,
            insightType: InsightType.INSIGHT,
            cutomizedView: true,
        });
        downloadCurrentRenderedNarrative(identifier)
            .then(() => {
                logWithTakt(TELEMETRY_ID.FORGE.VIZ_DOWNLOAD_SUCCESS, {
                    identifier,
                    insightType: InsightType.INSIGHT,
                    cutomizedView: true,
                });
            })
            .catch(() => {
                logWithTakt(TELEMETRY_ID.FORGE.VIZ_DOWNLOAD_ERROR, {
                    identifier,
                    insightType: InsightType.INSIGHT,
                    cutomizedView: true,
                });
            });
    };

    const InsightFamilyHeaderOption: InsightDetailOption = useMemo(() => {
        const handleShareClick = async () => {
            const baseUrl = window.location.origin + window.location.pathname;

            if (!identifier) {
                return;
            }

            const sharableIdentifier = await generateSharableLinkId(
                identifier,
                insightType,
            );

            const urlParams: Record<string, string> = {
                identifier: sharableIdentifier,
            };

            const searchParams = new URLSearchParams(window.location.search);
            const currentFilter = searchParams.get("filters");
            const sfId = searchParams.get("sfId");

            if (sfId) {
                urlParams.sfId = sfId;
            }

            if (currentFilter) {
                urlParams.filters = currentFilter;
            }

            const shareUrl = generateShareableUrl(baseUrl, urlParams);
            copyToClipboard(shareUrl, "share-url", COPY_MESSAGES.INSIGHT);
            const filters = useUnifiedSearch
                ? getUnifiedFilterTelemetryDetails()
                : getFilterTelemetryDetails();

            logWithTakt(TELEMETRY_ID.INSIGHT_SHARE_SUCCESS, {
                ...getFilterTelemetryDetails(),
                identifier,
                shareType: "insightFamily",
            });
        };

        const uxTreatmentsEnabled =
            FeatureFlagsStore.getState().uxTreatmentsOnSidePanel;

        if (uxTreatmentsEnabled) {
            const InsightFamilyHeader: React.FunctionComponent<
                InsightDetailSelectionProps
            > = (props: InsightDetailSelectionProps) => (
                <HeaderComponentBuilder
                    loading={downloadLoading}
                    headerComponents={[
                        <Header
                            numPages={numPages}
                            identifier={identifier}
                            insightType={insightType}
                        >
                            <ShareIcon
                                onClick={() => void handleShareClick()}
                                tooltipMessage={TOOLTIP_MESSAGES.INSIGHT}
                                testId="insight-family-share-icon"
                            />
                        </Header>,
                        <HeaderTitle title={title} />,
                        <HeaderCardDetails
                            details={details}
                            templateInfo={templateInfo}
                            selectedSlides={props.selectedSlides}
                            setSelectedSlides={props.setSelectedSlides}
                            numPages={numPages}
                            previewFileFormat={InsightPreviewFileFormats.FORGE}
                            identifier={identifier}
                            insightType={insightType ?? InsightType.INSIGHT}
                            buttonOverride={[
                                <NarrativeTranslateModalButton
                                    contentIdentifier={identifier}
                                />,
                                <DeckDecorationWithLanguageModalButton
                                    identifier={identifier}
                                    insightType={
                                        insightType ?? InsightType.INSIGHT
                                    }
                                    documentUrlAsync={getDocumentUrlAsync}
                                    placement={G4sPlacement.SIDE_PANEL}
                                />,
                            ]}
                        />,
                        businesslineId && icmVersion ? (
                            <IcmBusinesslineConfiguration
                                businesslineId={businesslineId}
                                icmVersion={icmVersion}
                            />
                        ) : (
                            <></>
                        ),
                    ]}
                />
            );
            return {
                type: InsightDetailType.SELECTION,
                component: InsightFamilyHeader,
            };
        } else {
            const InsightFamilyHeader: React.FunctionComponent = () => (
                <HeaderComponentBuilder
                    loading={downloadLoading}
                    headerComponents={[
                        <Header
                            numPages={numPages}
                            identifier={identifier}
                            insightType={insightType}
                        >
                            <ShareIcon
                                onClick={() => void handleShareClick()}
                                tooltipMessage={TOOLTIP_MESSAGES.INSIGHT}
                                testId="insight-family-share-icon"
                            />
                        </Header>,
                        <HeaderTitle title={title} />,
                        <HeaderCardDetails
                            details={details}
                            templateInfo={templateInfo}
                        />,
                        businesslineId && icmVersion ? (
                            <IcmBusinesslineConfiguration
                                businesslineId={businesslineId}
                                icmVersion={icmVersion}
                            />
                        ) : (
                            <></>
                        ),
                    ]}
                />
            );
            return {
                type: InsightDetailType.VIEW,
                component: InsightFamilyHeader,
            };
        }
    }, [identifier, numPages, details, templateInfo, downloadLoading]);

    const _handleForgeBetaToggle = () => {
        handleForgeBetaToggle?.();
        handleClose();
    };

    const InsightFamilyBodyOption: InsightDetailOption = useMemo(() => {
        const InsightFamilyBody: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => (
            <BodyComponentBuilder
                loading={false} // Loading rendering skeleton is handled by the Narrative component
                bodyComponents={[
                    ...(handleForgeBetaToggle
                        ? [
                              <ForgeBetaOptInToggle
                                  onToggle={_handleForgeBetaToggle}
                              />,
                          ]
                        : []),
                    <StyledNarrativeSelectorContainer
                        ref={narrativeSelectorContainerRef}
                    >
                        <NarrativeSelector
                            selectedSlides={props.selectedSlides}
                            setSelectedSlides={props.setSelectedSlides}
                            contentIdentifier={identifier}
                            contentType={ForgeContentType.INSIGHT}
                            setNumPages={setNumPages}
                        />
                    </StyledNarrativeSelectorContainer>,
                ]}
            />
        );
        return {
            type: InsightDetailType.SELECTION,
            component: InsightFamilyBody,
        };
    }, [identifier, areComponentsReady]);

    const getDocumentUrlAsync = React.useCallback(async () => {
        const SUMMARY_ASSET_ERROR_MESSAGE =
            "Failed to create asset used for summary";

        // get clients
        const [forgeClient, blinkClient] = await Promise.all([
            getForgeApiClient(),
            getBlinkApiClientV2(),
        ]);

        const cacheId = startOfHour(new Date()).toISOString();
        const resourceIdentifier = `${identifier}_${cacheId}`;

        try {
            // build forge pptx
            const forgePptxResponse = await forgeClient.getNarrativeAsPptx({
                contentIdentifier: identifier,
                contentType: ForgeContentType.INSIGHT,
            });

            // convert pptx -> pdf
            const forgePdfConversionResponse =
                await blinkClient.createAsyncOfficeConvert({
                    clientId: "IHUB",
                    applicationContext: "forge-summary",
                    format: "pdf",
                    resourceIdentifier,
                    inputLocation: forgePptxResponse.s3Location,
                    outputLocation: {
                        bucket: forgePptxResponse.s3Location?.bucket,
                        key: `${resourceIdentifier}.pdf`,
                    },
                });

            // wait for pdf to finish
            const { outputFileDownloadUrl } =
                await blinkClient.pollGetAsyncOfficeConvert({
                    executionId: forgePdfConversionResponse.execution,
                });

            if (isString(outputFileDownloadUrl)) return outputFileDownloadUrl;

            throw new Error(SUMMARY_ASSET_ERROR_MESSAGE);
        } catch (err) {
            console.error("Failed to create asset used for summary:", err);
            const errorMessage =
                err instanceof Error ? err.message : String(err);
            throw new Error(`${SUMMARY_ASSET_ERROR_MESSAGE}: ${errorMessage}`);
        }
    }, [identifier]);

    const InsightFamilyFooterOption: InsightDetailOption = useMemo(() => {
        const InsightFamilyFooter: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => (
            <FooterComponentBuilder
                loading={false}
                handleClose={handleClose}
                identifier={identifier}
                insightType={insightType}
                footerComponents={[
                    ...(!FeatureFlagsStore.getState().uxTreatmentsOnSidePanel
                        ? [
                              <NarrativeTranslateModalButton
                                  contentIdentifier={identifier}
                              />,
                              <DeckDecorationWithLanguageModalButton
                                  identifier={identifier}
                                  insightType={
                                      insightType ?? InsightType.INSIGHT
                                  }
                                  documentUrlAsync={getDocumentUrlAsync}
                                  placement={G4sPlacement.SIDE_PANEL}
                              />,
                          ]
                        : []),
                    <SplitButton
                        mainButtonLabel="Download"
                        mainButtonOnClick={() =>
                            handleCustomizedDownloadView(identifier)
                        }
                        menuPosition="top"
                    >
                        <SplitButtonItem
                            value="Download original view"
                            onClick={() => handleForgeVizDownload(identifier)}
                        >
                            Download Original View
                        </SplitButtonItem>
                    </SplitButton>,
                ]}
            />
        );
        return {
            type: InsightDetailType.SELECTION,
            component: InsightFamilyFooter,
        };
    }, [insightFamilyTrackingId, numPages, downloadLoading]);

    const setInsigtDetailComponents = useSetInsightDetailComponents({
        identifier,
        familyName,
        InsightHeaderOption: InsightFamilyHeaderOption,
        InsightBodyOption: InsightFamilyBodyOption,
        InsightFooterOption: InsightFamilyFooterOption,
        headerReady: areComponentsReady,
        bodyReady: areComponentsReady,
        footerReady: areComponentsReady,
        handleMount,
        handleClose,
    });

    return () => {
        setComponentsReady(true);
        setInsigtDetailComponents();
    };
};
